import React, { useMemo } from 'react';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import { getIntl } from '../../../../utils/intl';
import { APITypes } from '../../../../primary-record-types';
import useOrganizationCareTeamMembers from '../../../../hooks/api/useOrganizationCareTeamMembers';
import { getCareTeamMemberDescriptorDisplay } from '../../../../utils/careTeamUtils';
import { useFilterCareTeamMembers } from '../../../screens/Tabs/Profiles/DataDetailsScreen/useFilterCareTeamMembers';
import HeaderContainer from '../../../v2BaseComponents/HeaderContainer';
import MainContentContainer from '../../../v2BaseComponents/MainContentContainer';
import CareTeamMemberListCard from '../../../screens/Tabs/Profiles/DataDetailsScreen/CareTeamMemberListCard';
import SearchInput from '../../../v2BaseComponents/SearchInput';
import { PRScrollView } from '../../Scroll';

const intl = getIntl('copy-organization-care-team-members-to-clients', {
    resourceCareTeamMembers: 'Resource Care Team Members',
    sharingWithClients: 'Sharing with {count} clients',
    sharingWithClient: 'Sharing with 1 client',
    placeholder: 'Search',
});

export type UseCopyOrganizationCareTeamMemberToClientsStateReturnValue = {
    isInitializing: boolean;
    isSubmitting: boolean;
    selectedCareTeamMemberIds: string[];
    selectedCareTeamMembers: APITypes.organizationService.OrganizationCareTeamMemberDetails[];
    careTeamSelectOptions: APITypes.organizationService.OrganizationCareTeamMemberDetails[];
    onSubmit: () => void;
    canSubmit: boolean;
    onSelectedCareTeamMemberId: (careTeamMemberId: string) => void;
};

export type UseCopyOrganizationCareTeamMemberToClientsStateArgs = {
    onClose: () => void;
    destinationMedicalProfileIds: string[];
};

export const useCopyOrganizationCareTeamMemberToClientsState = (
    args: UseCopyOrganizationCareTeamMemberToClientsStateArgs,
): UseCopyOrganizationCareTeamMemberToClientsStateReturnValue => {
    const [selectedCareTeamMemberIds, setSelectedCareTeamMemberIds] = React.useState<string[]>([]);
    const {
        organizationCareTeamMembers,
        isLoadingOrganizationCareTeamMembers,
        copyCareTeamMemberToClients,
        isCopyingCareTeamMemberToClients,
    } = useOrganizationCareTeamMembers({
        onCopyCareTeamMemberToClientsSuccess: args.onClose,
    });

    const sortedCareTeamMembers = useMemo(() => {
        return [...(organizationCareTeamMembers || [])].sort((a, b) => {
            return getCareTeamMemberDescriptorDisplay(a).localeCompare(getCareTeamMemberDescriptorDisplay(b));
        });
    }, [organizationCareTeamMembers]);
    const selectedCareTeamMembers = useMemo(() => {
        return (
            organizationCareTeamMembers?.filter((careTeamMember) =>
                selectedCareTeamMemberIds.includes(careTeamMember.careTeamMemberId),
            ) || null
        );
    }, [organizationCareTeamMembers, selectedCareTeamMemberIds]);

    return {
        isInitializing: isLoadingOrganizationCareTeamMembers,
        isSubmitting: isCopyingCareTeamMemberToClients,
        selectedCareTeamMemberIds: selectedCareTeamMemberIds,
        selectedCareTeamMembers,
        careTeamSelectOptions: sortedCareTeamMembers,
        onSubmit: () => {
            if (selectedCareTeamMemberIds.length) {
                copyCareTeamMemberToClients({
                    organizationCareTeamMemberIds: selectedCareTeamMemberIds,
                    destinationMedicalProfileIds: args.destinationMedicalProfileIds,
                });
            }
        },
        canSubmit: !!selectedCareTeamMemberIds.length && !isCopyingCareTeamMemberToClients,
        onSelectedCareTeamMemberId: (careTeamMemberId: string) => {
            setSelectedCareTeamMemberIds((prev) => {
                if (prev.includes(careTeamMemberId)) {
                    return prev.filter((id) => id !== careTeamMemberId);
                }
                return [...prev, careTeamMemberId];
            });
        },
    };
};

export type CopyOrganizationCareTeamMemberToClientsOwnProps = {
    destinationMedicalProfileIds: string[];
    selectedCareTeamMemberIds: string[];
    selectedCareTeamMembers: APITypes.organizationService.OrganizationCareTeamMemberDetails[];
    careTeamSelectOptions: APITypes.organizationService.OrganizationCareTeamMemberDetails[];
    onSelectedCareTeamMemberId: (careTeamMemberId: string) => void;
    flatCards?: boolean;
    isDisabled?: boolean;
};

export type CopyOrganizationCareTeamMemberToClientsProps = CopyOrganizationCareTeamMemberToClientsOwnProps;

export type UseCopyOrganizationCareTeamMemberToClientsState = (
    props: CopyOrganizationCareTeamMemberToClientsProps,
) => UseCopyOrganizationCareTeamMemberToClientsStateReturnValue;

const CopyOrganizationCareTeamMemberToClients: React.FC<CopyOrganizationCareTeamMemberToClientsProps> = ({
    selectedCareTeamMemberIds,
    careTeamSelectOptions,
    isDisabled,
    onSelectedCareTeamMemberId,
    flatCards,
}) => {
    const [searchText, setSearchText] = React.useState('');
    const filteredOptions = useFilterCareTeamMembers(careTeamSelectOptions, searchText);

    return (
        <Stack vertical fullWidth fullHeight>
            <HeaderContainer>
                <SearchInput
                    placeholder={intl('placeholder')}
                    value={searchText}
                    onChange={setSearchText}
                    isDisabled={isDisabled}
                    testID="care-team-member-share-search"
                />
            </HeaderContainer>
            <PRScrollView style={{ width: '100%', minHeight: 0 }}>
                <MainContentContainer>
                    <Stack
                        vertical
                        fullWidth
                        space={contentStackSpacing}
                        flex={1}
                        style={{ minHeight: '100%' }}
                        justify="space-between"
                    >
                        {filteredOptions.map((careTeamMember) => (
                            <CareTeamMemberListCard
                                careTeamMember={careTeamMember}
                                isSelectable
                                isSelected={selectedCareTeamMemberIds.includes(careTeamMember.careTeamMemberId)}
                                onSelect={() => onSelectedCareTeamMemberId(careTeamMember.careTeamMemberId)}
                                profileId={null}
                                flat={flatCards}
                                testID="care-team-member-share-list-card"
                            />
                        ))}
                    </Stack>
                </MainContentContainer>
            </PRScrollView>
        </Stack>
    );
};

export default React.memo(CopyOrganizationCareTeamMemberToClients);
